import { findFirstFreeSlot } from '../gridDashboardUtils';
import { getMyWidgetMeta } from './myWidgetRegistry';
import { getMarketWidgetMeta } from '../../market/marketWidgetRegistry';

const metaFor = (type) => getMyWidgetMeta(type) || getMarketWidgetMeta(type);

/**
 * Starter dashboards built from the user's own accounts, so no one starts from an
 * empty page. Picking one creates a normal personal dashboard they can change.
 */
// Each widget has an explicit size so the layout tiles cleanly on the 12-column
// grid (the gallery previews exactly this layout).
export const MY_DASHBOARD_TEMPLATES = [
  {
    id: 'master',
    name: 'Master dashboard',
    featured: true,
    description: 'Every account plus the market: combined equity and P&L, allocation, trades and positions, next to a live chart, heatmap, news and the economic calendar.',
    widgets: [
      { type: 'mkTickerTape', config: { watchlist: 'indices' }, size: { w: 12, h: 1 } },
      { type: 'myKpi', config: { metric: 'equity', account: 'all', range: '3M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'netPnl', account: 'all', range: '3M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'todayPnl', account: 'all', range: '1M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'profitFactor', account: 'all', range: '3M' }, size: { w: 3, h: 3 } },
      // Trade Watcher row: only when the person has NinjaTrader accounts on Trade Watcher.
      { type: 'twToday', config: {}, size: { w: 12, h: 2 }, when: 'tradeWatcher' },
      { type: 'myInsights', config: {}, size: { w: 12, h: 4 } },
      { type: 'myEquity', config: { account: 'all', range: '3M' }, size: { w: 8, h: 5 } },
      { type: 'myAllocation', config: { groupBy: 'broker' }, size: { w: 4, h: 5 } },
      { type: 'twDailyPnl', config: { days: 90 }, size: { w: 8, h: 5 }, when: 'tradeWatcher' },
      { type: 'twAlerts', config: {}, size: { w: 4, h: 5 }, when: 'tradeWatcher' },
      { type: 'myPnlCalendar', config: { account: 'all' }, size: { w: 6, h: 7 } },
      { type: 'mkChart', config: { symbol: 'AMEX:SPY', interval: 'D' }, size: { w: 6, h: 7 } },
      { type: 'myTrades', config: { account: 'all' }, size: { w: 4, h: 6 } },
      { type: 'myPositions', config: { account: 'all' }, size: { w: 4, h: 6 } },
      { type: 'mkNews', config: { feed: 'all' }, size: { w: 4, h: 6 } },
      { type: 'mkHeatmap', config: { market: 'SPX500' }, size: { w: 8, h: 5 } },
      { type: 'mkCalendar', config: {}, size: { w: 4, h: 5 } },
    ],
  },
  {
    id: 'tradeWatcher',
    name: 'Trade Watcher',
    description: 'Your NinjaTrader accounts live: running P&L, naked and offline checks, daily P&L with streaks, a calendar, every account and recent alerts.',
    widgets: [
      { type: 'mkTickerTape', config: { watchlist: 'futures' }, size: { w: 12, h: 1 } },
      { type: 'twToday', config: {}, size: { w: 12, h: 2 } },
      { type: 'twDailyPnl', config: { days: 90 }, size: { w: 8, h: 5 } },
      { type: 'twAlerts', config: {}, size: { w: 4, h: 5 } },
      { type: 'twAccounts', config: {}, size: { w: 7, h: 6 } },
      { type: 'twCalendar', config: {}, size: { w: 5, h: 6 } },
      // NQ itself (CME_MINI:NQ1!) isn't allowed in free TradingView embeds; OANDA's
      // Nasdaq 100 price tracks the same market nearly around the clock.
      { type: 'mkChart', config: { symbol: 'OANDA:NAS100USD', interval: '15' }, size: { w: 8, h: 6 } },
      { type: 'mkCalendar', config: {}, size: { w: 4, h: 6 } },
    ],
  },
  {
    id: 'futures',
    name: 'Futures & prop trader',
    description: 'Today’s P&L, live NinjaTrader accounts, your P&L calendar and recent trades.',
    widgets: [
      { type: 'myKpi', config: { metric: 'todayPnl', account: 'all', range: '1M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'netPnl', account: 'all', range: '1M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'winDayRate', account: 'all', range: '1M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'maxDrawdown', account: 'all', range: '1M' }, size: { w: 3, h: 3 } },
      { type: 'myPnlCalendar', config: { account: 'all' }, size: { w: 6, h: 7 } },
      { type: 'myLiveAccounts', config: {}, size: { w: 6, h: 3 } },
      { type: 'myTrades', config: { account: 'all' }, size: { w: 6, h: 4 } },
      { type: 'myEquity', config: { account: 'all', range: '3M' }, size: { w: 12, h: 5 } },
    ],
  },
  {
    id: 'investor',
    name: 'Stock & crypto investor',
    description: 'Account value, allocation across brokers, open positions and growth over time.',
    widgets: [
      { type: 'myKpi', config: { metric: 'equity', account: 'all', range: '1Y' }, size: { w: 4, h: 3 } },
      { type: 'myKpi', config: { metric: 'netPnl', account: 'all', range: 'YTD' }, size: { w: 4, h: 3 } },
      { type: 'myKpi', config: { metric: 'maxDrawdown', account: 'all', range: '1Y' }, size: { w: 4, h: 3 } },
      { type: 'myEquity', config: { account: 'all', range: '1Y' }, size: { w: 8, h: 5 } },
      { type: 'myAllocation', config: { groupBy: 'broker' }, size: { w: 4, h: 5 } },
      { type: 'myPositions', config: { account: 'all' }, size: { w: 12, h: 5 } },
    ],
  },
  {
    id: 'all',
    name: 'All my accounts',
    description: 'Everything in one place: combined equity, calendar, positions and trades.',
    widgets: [
      { type: 'myKpi', config: { metric: 'equity', account: 'all', range: '3M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'netPnl', account: 'all', range: '3M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'todayPnl', account: 'all', range: '1M' }, size: { w: 3, h: 3 } },
      { type: 'myKpi', config: { metric: 'profitFactor', account: 'all', range: '3M' }, size: { w: 3, h: 3 } },
      { type: 'myEquity', config: { account: 'all', range: '3M' }, size: { w: 8, h: 5 } },
      { type: 'myAllocation', config: { groupBy: 'account' }, size: { w: 4, h: 5 } },
      { type: 'myPnlCalendar', config: { account: 'all' }, size: { w: 6, h: 7 } },
      { type: 'myTrades', config: { account: 'all' }, size: { w: 6, h: 7 } },
    ],
  },
  {
    id: 'market',
    name: 'Market watch',
    description: 'Live quotes, a full chart, today’s heatmap, top movers, news and the economic calendar.',
    widgets: [
      { type: 'mkTickerTape', config: { watchlist: 'indices' }, size: { w: 12, h: 1 } },
      { type: 'mkChart', config: { symbol: 'AMEX:SPY', interval: 'D' }, size: { w: 8, h: 6 } },
      { type: 'mkCalendar', config: {}, size: { w: 4, h: 6 } },
      { type: 'mkHeatmap', config: { market: 'SPX500' }, size: { w: 6, h: 5 } },
      { type: 'mkMovers', config: {}, size: { w: 3, h: 5 } },
      { type: 'mkNews', config: { feed: 'all' }, size: { w: 3, h: 5 } },
    ],
  },
];

let seq = 0;
function newId() {
  seq += 1;
  return `w_${Date.now().toString(36)}_${seq}`;
}

/**
 * { name, layout, widgets } ready for POST /api/dashboards.
 * has.tradeWatcher: include the widgets marked `when: 'tradeWatcher'` (default
 * true, so the gallery preview shows the whole layout).
 */
export function buildDashboardFromTemplate(templateId, has = {}) {
  const t = MY_DASHBOARD_TEMPLATES.find((x) => x.id === templateId);
  if (!t) return null;
  const widgets = [];
  const sizes = {};
  const placed = [];
  t.widgets.forEach((w) => {
    if (w.when && has[w.when] === false) return;
    const meta = metaFor(w.type);
    if (!meta) return;
    const id = newId();
    const size = w.size || meta.defaultSize;
    const slot = findFirstFreeSlot(size.w, size.h, placed);
    placed.push(slot);
    sizes[id] = slot;
    widgets.push({ id, type: w.type, config: { ...meta.defaultConfig(), ...w.config } });
  });
  return {
    name: t.name,
    layout: { order: widgets.map((w) => w.id), sizes, hidden: [] },
    widgets,
  };
}

/**
 * The Trade Watcher section for an existing dashboard ("Add Trade Watcher"):
 * today's strip, then daily P&L next to alerts, at the TOP of the dashboard.
 * Everything already there moves down by `height` rows (see shiftSizes).
 */
export const TRADE_WATCHER_SECTION_HEIGHT = 7;

export function tradeWatcherSection() {
  const rows = [
    { type: 'twToday', config: {}, slot: { col: 1, row: 1, w: 12, h: 2 } },
    { type: 'twDailyPnl', config: { days: 90 }, slot: { col: 1, row: 3, w: 8, h: 5 } },
    { type: 'twAlerts', config: {}, slot: { col: 9, row: 3, w: 4, h: 5 } },
  ];
  const widgets = [];
  const sizes = {};
  rows.forEach((w) => {
    const meta = metaFor(w.type);
    if (!meta) return;
    const id = newId();
    sizes[id] = { ...w.slot };
    widgets.push({ id, type: w.type, config: { ...meta.defaultConfig(), ...w.config } });
  });
  return { widgets, sizes, height: TRADE_WATCHER_SECTION_HEIGHT };
}

/** Move every placement down by `rows` (making room at the top). */
export function shiftSizes(sizes, rows) {
  const out = {};
  Object.entries(sizes || {}).forEach(([id, p]) => {
    out[id] = p && Number.isFinite(Number(p.row)) ? { ...p, row: Number(p.row) + rows } : p;
  });
  return out;
}
